/**
 * CountryCodePicker — the "+91 ▾" prefix on the phone field (Figma's
 * "Dropdown / Country code"): a searchable list, filtered by country name
 * or dial code, with a checkmark on the current selection.
 */
import { useState } from 'react';
import { CaretDown, Check, MagnifyingGlass } from '../../../design-system/icons';
import { Popover, PopoverContent, PopoverTrigger } from '../../../components/ui/popover';
import { Input } from '../../../components/ui/input';
import { CountryFlag } from '../../../components/common/CountryFlag';
import { COUNTRY_DIAL_CODES, DEFAULT_COUNTRY_DIAL_CODE, type CountryDialCode } from './countryDialCodes';

const FALLBACK_COUNTRY: CountryDialCode = { code: 'IN', name: 'India', dialCode: DEFAULT_COUNTRY_DIAL_CODE };

export interface CountryCodePickerProps {
  /** Dial code, e.g. "+91". Falls back to the list's first entry if unknown. */
  value: string;
  onChange: (dialCode: string) => void;
  disabled?: boolean;
  /** Limit the list to these dial codes (default: every country). */
  dialCodes?: readonly string[];
}

export function CountryCodePicker({ value, onChange, disabled = false, dialCodes }: CountryCodePickerProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const selected = COUNTRY_DIAL_CODES.find((c) => c.dialCode === value) ?? FALLBACK_COUNTRY;
  const needle = query.trim().toLowerCase();
  const filtered = COUNTRY_DIAL_CODES.filter(
    (c) => (!dialCodes || dialCodes.includes(c.dialCode)) && `${c.name} ${c.dialCode}`.toLowerCase().includes(needle),
  );

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) setQuery('');
      }}
    >
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          aria-label="Country code"
          className="flex h-9 shrink-0 items-center gap-1.5 rounded-l-md border border-r-0 border-border bg-background px-2.5 text-body-sm-regular disabled:opacity-50"
        >
          <CountryFlag code={selected.code} />
          <span>{selected.dialCode}</span>
          <CaretDown size={12} className="text-muted-foreground" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-64 p-2">
        <div className="relative mb-2">
          <MagnifyingGlass size={14} className="absolute top-1/2 left-2.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            autoFocus
            placeholder="Search country or code"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="h-8 pl-7 text-body-sm-regular"
          />
        </div>
        <ul className="flex max-h-56 flex-col gap-0.5 overflow-y-auto">
          {filtered.map((c) => (
            <li key={c.code}>
              <button
                type="button"
                onClick={() => {
                  onChange(c.dialCode);
                  setOpen(false);
                  setQuery('');
                }}
                className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-body-sm-regular hover:bg-muted"
              >
                <CountryFlag code={c.code} />
                <span className="flex-1 truncate">{c.name}</span>
                <span className="text-muted-foreground">{c.dialCode}</span>
                {c.dialCode === selected.dialCode && <Check size={14} />}
              </button>
            </li>
          ))}
          {filtered.length === 0 && (
            <li className="px-2 py-1.5 text-body-sm-regular text-muted-foreground">No matches</li>
          )}
        </ul>
      </PopoverContent>
    </Popover>
  );
}
